import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import '../styles/Notifications.css';
import { formatDate, MiniNav, FileUpload, Text } from './FieldComponents';
import { ArrowRight, CheckSquare, ExternalLink, Link, Printer, MessageSquare, Phone, Mail as Email, FileText, Paperclip, UserCheck, X, Search, Briefcase, Building, Maximize2, Minimize2, ChevronDown, Download, Eye, Edit2, Info, AtSign, AlertTriangle } from 'lucide-react';
import { Assignment, AssignmentIndOutlined, Chat } from '@mui/icons-material';
import Modal from './Modal';
import EnhancedSearch from './EnhancedSearch';
import { useNavVariant } from '../App';
import { formatLastModified } from './NestedFeed';
import { MailIntake } from './MailIntake';
import {
    createCommunicationRequestId,
    isSmsConfirmationPending,
    SMS_CONFIRMATION_PENDING_MESSAGE
} from '../utils/communicationRequestId';
import { ensureCaseDbCsrfToken, withCsrfHeaders } from '../utils/csrf';

export const getTimeAgo = (dateString) => {
    const now = new Date();

    const parsedDate = (() => {
        if (!dateString) return new Date(NaN);
        const [, iso] = formatLastModified(String(dateString));
        if (iso) {
            const parsed = new Date(iso);
            if (!isNaN(parsed.getTime())) return parsed;
        }
        return new Date(dateString);
    })();
    
    const diff = (now - parsedDate) / 1000;
  
    if (!isFinite(diff)) {
        return `just now`;
    }

    if (diff < 0) {
        return `just now`;
    }

    if (diff < 60) {
        return `${Math.floor(diff)}s ago`;
    } else if (diff < 3600) {
        return `${Math.floor(diff / 60)}m ago`;
    } else if (diff < 86400) {
        return `${Math.floor(diff / 3600)}h ago`;
    } else if (diff < 2592000) {
        return `${Math.floor(diff / 86400)}d ago`;
    } else {
        return `${Math.floor(diff / 2592000)}mo ago`;
    }
};

export const getNotificationTypeIcon = (type, size = 18, notification = null) => {
    const normalizedType = String(type || '').trim().toLowerCase().replace(/[\s-]+/g, '_');
    const notificationContent = [
        notification?.body,
        notification?.content,
        notification?.message,
        notification?.title
    ].find((value) => typeof value === 'string' && value.trim());

    if (notificationContent?.replace(/<[^>]*>/g, '').trim().toLowerCase().startsWith('inbound sms')) {
        return <Chat style={{ fontSize: size }} />;
    }

    switch (normalizedType) {
        case 'case_analysis_complete':
        case 'jane_complete':
            return <CheckSquare size={size} />;
        case 'jane_action_required':
            return <Info size={size} />;
        case 'jane_failed':
            return <AlertTriangle size={size} />;
        case 'task':
        case 'tasks':
            return <CheckSquare size={size} />;
        case 'assignment':
            return <AssignmentIndOutlined style={{ fontSize: size }} />;
        case 'mention':
            return <AtSign size={size} />;
        case 'email':
        case 'emails':
        case 'email_inbound':
        case 'inbound_email':
            return <Email size={size} />;
        case 'sms':
        case 'text':
        case 'texts':
        case 'mms':
        case 'chat':
            return <MessageSquare size={size} />;
        case 'fax':
        case 'faxes':
            return <Printer size={size} />;
        case 'call':
        case 'calls':
        case 'phone':
            return <Phone size={size} />;
        case 'note':
        case 'notes':
            return <Edit2 size={size} />;
        case 'reminder':
        case 'reminders':
            return <Info size={size} />;
        case 'case_team':
        case 'team':
            return <UserCheck size={size} />;
        case 'lead_marketing_source':
        case 'lead':
        case 'lead_intake':
            return <Briefcase size={size} />;
        case 'settlement':
        case 'settlement_review':
            return <Building size={size} />;
        case 'docket':
        case 'docket_review':
            return <FileText size={size} />;
        case 'esignature':
        case 'esignatures':
        case 'e_signature':
        case 'e_signatures':
            return <Paperclip size={size} />;
        case 'communication_failure':
        case 'sms_failure':
        case 'fax_failure':
        case 'email_failure':
            return <AlertTriangle size={size} />;
        default:
            return <Assignment style={{ fontSize: size }} />;
    }
};

export const Notifications = ({ notifications, fetchNotifications }) => {
    const navigate = useNavigate();
    const navVariant = useNavVariant();
    
    const [users, setUsers] = useState([]);
    const [seenNotifications, setSeenNotifications] = useState([]);
    const [inboxNav, setInboxNav] = useState(() => new URLSearchParams(window.location.search).get('view') === 'mail' ? 2 : 0);
    const [screenWidth, setScreenWidth] = useState(window.innerWidth);
    const [mail, setMail] = useState([]);
    const [loadingMail, setLoadingMail] = useState(false);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [assigningItem, setAssigningItem] = useState(null);
    const [selectedAssignment, setSelectedAssignment] = useState(null);
    const [assigningInProgress, setAssigningInProgress] = useState(false);
    const [filterFeedType, setFilterFeedType] = useState(0);
    const [showCommunicationModal, setShowCommunicationModal] = useState(false);
    const [communicationType, setCommunicationType] = useState('fax'); // 'fax' or 'sms'
    const [communicationData, setCommunicationData] = useState({
        phoneNumber: '',
        subject: '',
        content: '',
        selectedCase: null,
        files: []
    });
    const [sendingCommunication, setSendingCommunication] = useState(false);
    const [smsPendingConfirmation, setSmsPendingConfirmation] = useState(false);
    const [replyingTo, setReplyingTo] = useState(null); // Track which item is being replied to
    const [replyContent, setReplyContent] = useState('');
    const [replyExpanded, setReplyExpanded] = useState(false);
    const [sendingReply, setSendingReply] = useState(false);
    const [fileDropdownOpen, setFileDropdownOpen] = useState(null); // Track which file dropdown is open (format: "itemId_attachmentIdx")
    const [showRenameFileModal, setShowRenameFileModal] = useState(false);
    const [fileToRename, setFileToRename] = useState(null);
    const [newFileName, setNewFileName] = useState('');
    const [fileExtension, setFileExtension] = useState(''); // Store extension separately
    const [renamingFile, setRenamingFile] = useState(false);
    const [showPropertiesModal, setShowPropertiesModal] = useState(false);
    const [fileProperties, setFileProperties] = useState(null);
    const [dropdownPosition, setDropdownPosition] = useState({});
    const [dropdownPositionAbove, setDropdownPositionAbove] = useState({});
    const [markingAllRead, setMarkingAllRead] = useState(false);
    const [markAllReadError, setMarkAllReadError] = useState('');
    const dropdownRef = useRef(null);
    const smsSendLockRef = useRef(false);
    const smsRequestIdRef = useRef(null);

    useEffect(() => {
        smsRequestIdRef.current = null;
        setSmsPendingConfirmation(false);
    }, [communicationType, communicationData]);

    useEffect(() => {
        const handleResize = () => setScreenWidth(window.innerWidth);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    
    const fetchSeenNotifications = async () => {
        try {
            const response = await fetch(`https://api.casedb.co/notifications.php?seen=true&time=${new Date().getTime()}`, { credentials: 'include' });
            const data = await response.json();
            if (response.ok) {
                setSeenNotifications(data.notifications || []);
            }
        } catch (error) {
            console.error("Error fetching notifications:", error);
        }
    };

    useEffect(() => {
        fetchSeenNotifications();
    }, []);

    useEffect(() => {
        const fetchUsers = async () => {
            try {
                const response = await fetch(`https://api.casedb.co/user.php?time=${new Date().getTime()}`, { credentials: 'include' });
                const data = await response.json();
                if (response.ok) {
                    const processedUsers = data.users.map(user => ({
                        ...user,
                        user: user.user || user.name.toLowerCase().replace(/\s+/g, '_')
                    }));
                    setUsers(processedUsers);
                }
            } catch (error) {
                console.error("Error fetching users:", error);
            }
        };
        
        fetchMail();
        fetchUsers();

        document.title = "Notifications | CaseDB";
    }, []);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (fileDropdownOpen && dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                // Check if click is on a chevron element
                const clickedChevron = event.target.closest('[data-dropdown-id]');
                if (!clickedChevron) {
                    setFileDropdownOpen(null);
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [fileDropdownOpen]);

    const readNotification = async (notification) => {
        try {
            const response = await fetch(`https://api.casedb.co/notifications.php?read=${notification.id}`, {
                method: 'GET',
                credentials: 'include'
            });
            const data = await response.json();
            if (data.success) {
                fetchNotifications();
            }
        } catch (error) {
            console.error("Error marking notification as read:", error);
        }

        if (notification.url) {
            // Construct the base URL with proper leading slash
            const baseUrl = notification.url.startsWith("/") ? notification.url : `/${notification.url}`;
            
            // Add foreign_identifier as query parameter if it exists and isn't already in the URL
            let finalUrl = baseUrl;
            if (notification.foreign_identifier && !baseUrl.includes('key=')) {
                const separator = baseUrl.includes('?') ? '&' : '?';
                finalUrl = `${baseUrl}${separator}key=${notification.foreign_identifier}`;
            }
            
            console.log('Final URL:', finalUrl);

            navigate(finalUrl);
        }
    }

    const markAllNotificationsRead = async () => {
        if (markingAllRead || notifications.length === 0) return;

        setMarkingAllRead(true);
        setMarkAllReadError('');
        try {
            await ensureCaseDbCsrfToken();
            const response = await fetch('https://api.casedb.co/notifications.php', {
                method: 'POST',
                credentials: 'include',
                headers: withCsrfHeaders({ 'Content-Type': 'application/json' }, 'POST'),
                body: JSON.stringify({ action: 'mark_all_read' })
            });
            const data = await response.json();
            if (!response.ok || !data.success) {
                throw new Error(data.message || 'Notifications could not be marked as read.');
            }

            await Promise.all([
                Promise.resolve(fetchNotifications({ force: true })),
                fetchSeenNotifications()
            ]);
        } catch (error) {
            console.error('Error marking all notifications as read:', error);
            setMarkAllReadError(error.message || 'Notifications could not be marked as read.');
        } finally {
            setMarkingAllRead(false);
        }
    };

    const fetchMail = async () => {
        setLoadingMail(true);
        try {
            const response = await fetch(`https://api.casedb.co/mailroom.php?time=${new Date().getTime()}`, { credentials: 'include' });
            const data = await response.json();
            console.log('Mailroom API response:', data); // Debug log
            if (response.ok) {
                setMail(data.unassigned_items || []);
                console.log('Mail items set:', data.unassigned_items || []); // Debug log
            }
        } catch (error) {
            console.error("Error fetching mail:", error);
        } finally {
            setLoadingMail(false);
        }
    }

    const formatContentWithMentions = (content) => {
        if (!content || !users || users.length === 0) return content;

        // Create a regex pattern to match @username
        const mentionPattern = /@([a-zA-Z0-9_]+)/g;
        
        // Replace @mentions with wrapped spans
        return content.replace(mentionPattern, (match, username) => {
            // Check if the username exists in the users array
            const mentionedUser = users.find(u => u.user === username);
            if (mentionedUser) {
                return `<span class="tag-mention">@${username}</span>`;
            }
            return match; // Return original if no match
        });
    };

    const handleNotificationClick = (notification) => {
        if (notification.url) {
            // Construct the base URL with proper leading slash
            const baseUrl = notification.url.startsWith("/") ? notification.url : `/${notification.url}`;
            
            // Add foreign_identifier as query parameter if it exists and isn't already in the URL
            let finalUrl = baseUrl;
            if (notification.foreign_identifier && !baseUrl.includes('key=')) {
                const separator = baseUrl.includes('?') ? '&' : '?';
                finalUrl = `${baseUrl}${separator}key=${notification.foreign_identifier}`;
            }
            
            navigate(finalUrl);
        }
    };

    const getTypeIcon = (type) => {
        switch (type) {
            case 'faxes':
                return <Printer size={18} />;
            case 'texts':
                return <MessageSquare size={18} />;
            case 'calls':
                return <Phone size={18} />;
            case 'emails':
                return <Email size={18} />;
            default:
                return <FileText size={18} />;
        }
    };

    const handleAssignClick = (item) => {
        setAssigningItem(item);
        setShowAssignModal(true);
        setSelectedAssignment(null);
    };

    const assignMailItem = async () => {
        if (!selectedAssignment || !assigningItem || assigningInProgress) return;

        setAssigningInProgress(true);

        try {
            await ensureCaseDbCsrfToken();
            const assignmentData = {
                action: 'assign',
                item_id: assigningItem.id,
            };

            // Add the correct parameter based on assignment type
            if (selectedAssignment.type === 'case') {
                assignmentData.case_id = selectedAssignment.value;
            } else if (selectedAssignment.type === 'lead') {
                assignmentData.lead_id = selectedAssignment.value;
            }

            const response = await fetch(`https://api.casedb.co/mailroom.php`, {
                method: 'POST',
                headers: withCsrfHeaders({
                    'Content-Type': 'application/json',
                }, 'POST'),
                credentials: 'include',
                body: JSON.stringify(assignmentData)
            });
            
            if (response.ok) {
                setShowAssignModal(false);
                setAssigningItem(null);
                setSelectedAssignment(null);
                fetchMail(); // Refresh the mail list
            } else {
                const errorData = await response.json();
                console.error("Assignment failed:", errorData);
            }
        } catch (error) {
            console.error("Error assigning mail item:", error);
        } finally {
            setAssigningInProgress(false);
        }
    };

    const deleteMailItem = async (itemId) => {
        try {
            await ensureCaseDbCsrfToken();
            const response = await fetch(`https://api.casedb.co/mailroom.php`, {
                method: 'POST',
                headers: withCsrfHeaders({
                    'Content-Type': 'application/json',
                }, 'POST'),
                credentials: 'include',
                body: JSON.stringify({
                    action: 'delete',
                    item_id: itemId
                })
            });
            
            if (response.ok) {
                fetchMail(); // Refresh the mail list
            }
        } catch (error) {
            console.error("Error deleting mail item:", error);
        }
    };

    const handleReplySubmit = async (itemId) => {
        if (!replyContent.trim() || sendingReply) return;

        setSendingReply(true);

        try {
            await ensureCaseDbCsrfToken();
            const response = await fetch(`https://api.casedb.co/mailroom.php`, {
                method: 'POST',
                headers: withCsrfHeaders({
                    'Content-Type': 'application/json',
                }, 'POST'),
                credentials: 'include',
                body: JSON.stringify({
                    action: 'reply',
                    reply_to_id: itemId,
                    content: replyContent,
                    subject: 'Re: Mailroom Item'
                })
            });
            
            const result = await response.json();
            
            if (response.ok && result.success) {
                // Clear reply state
                setReplyContent('');
                setReplyExpanded(false);
                setReplyingTo(null);
                // Refresh mail to show new reply
                fetchMail();
            } else {
                console.error("Reply failed:", result.message);
                alert("Failed to send reply: " + (result.message || "Unknown error"));
            }
        } catch (error) {
            console.error("Error sending reply:", error);
            alert("Failed to send reply");
        } finally {
            setSendingReply(false);
        }
    };

    const handleReplyFocus = (itemId) => {
        setReplyingTo(itemId);
        setReplyExpanded(true);
    };

    const handleReplyCancel = () => {
        setReplyContent('');
        setReplyExpanded(false);
        setReplyingTo(null);
    };

    const handleCommunicationClick = (type) => {
        setCommunicationType(type);
        setSmsPendingConfirmation(false);
        setCommunicationData({
            phoneNumber: '',
            subject: '',
            content: '',
            selectedCase: null,
            files: []
        });
        setShowCommunicationModal(true);
    };

    const handleFileUpload = (e) => {
        const files = Array.from(e.target.files);
        setCommunicationData(prev => ({
            ...prev,
            files: files
        }));
    };

    const sendCommunication = async () => {
        const isSmsSubmission = communicationType === 'sms';
        if (isSmsSubmission && (smsSendLockRef.current || smsPendingConfirmation)) return;
        if (!communicationData.phoneNumber || !communicationData.content) {
            alert('Phone number and content are required');
            return;
        }

        if (communicationType === 'fax' && communicationData.files.length === 0) {
            alert('At least one file is required for fax');
            return;
        }

        if (isSmsSubmission) smsSendLockRef.current = true;
        setSendingCommunication(true);

        try {
            const formData = new FormData();
            
            if (communicationType === 'fax') {
                formData.append('fax_number', communicationData.phoneNumber);
                // Convert files to base64 for fax-fixed.php
                if (communicationData.files.length > 0) {
                    const file = communicationData.files[0]; // Take first file for fax
                    const base64 = await fileToBase64(file);
                    formData.append('file_data', base64);
                    formData.append('file_name', file.name);
                }
            } else {
                formData.append('phone_number', communicationData.phoneNumber);
                formData.append('message', communicationData.content);
                const requestId = smsRequestIdRef.current || createCommunicationRequestId();
                smsRequestIdRef.current = requestId;
                formData.append('request_id', requestId);
                // Convert files to base64 for MMS
                if (communicationData.files.length > 0) {
                    const fileDataArray = [];
                    const fileNameArray = [];
                    for (let file of communicationData.files) {
                        const base64 = await fileToBase64(file);
                        fileDataArray.push(base64);
                        fileNameArray.push(file.name);
                    }
                    fileDataArray.forEach((data, index) => {
                        formData.append(`file_data[${index}]`, data);
                        formData.append(`file_name[${index}]`, fileNameArray[index]);
                    });
                }
            }

            if (communicationData.subject) {
                formData.append('subject', communicationData.subject);
            }
            
            if (communicationData.content) {
                formData.append('content', communicationData.content);
            }

            // Add case/lead if selected
            if (communicationData.selectedCase) {
                if (communicationData.selectedCase.type === 'case') {
                    formData.append('case_id', communicationData.selectedCase.value);
                } else if (communicationData.selectedCase.type === 'lead') {
                    formData.append('lead_id', communicationData.selectedCase.value);
                }
            }

            const response = await fetch('https://api.casedb.co/fax-fixed.php', {
                method: 'POST',
                credentials: 'include',
                body: formData
            });

            const result = await response.json();

            if (isSmsSubmission && isSmsConfirmationPending(result)) {
                setSmsPendingConfirmation(true);
            } else if (response.ok && result.success) {
                if (isSmsSubmission) smsRequestIdRef.current = null;
                if (isSmsSubmission) setSmsPendingConfirmation(false);
                setShowCommunicationModal(false);
                fetchMail(); // Refresh the mail list
                alert(`${communicationType === 'fax' ? 'Fax' : 'SMS'} sent successfully!`);
            } else {
                if (isSmsSubmission && result?.retry_with_new_request_id) {
                    smsRequestIdRef.current = null;
                }
                throw new Error(result.error || `Failed to send ${communicationType}`);
            }
        } catch (error) {
            console.error(`Error sending ${communicationType}:`, error);
            alert(`Failed to send ${communicationType}: ${error.message}`);
        } finally {
            if (isSmsSubmission) smsSendLockRef.current = false;
            setSendingCommunication(false);
        }
    };

    const fileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result.split(',')[1]); // Remove data:image/jpeg;base64, prefix
            reader.onerror = error => reject(error);
        });
    };

    const calculateDropdownPosition = (itemId, attachmentIdx, dropdownElement = null) => {
        const dropdownId = `${itemId}_${attachmentIdx}`;
        
        // Find the chevron element that was clicked
        const chevronElement = document.querySelector(`[data-dropdown-id="${dropdownId}"]`);
        if (!chevronElement) return { showAbove: false, left: 0, top: 0 };
        
        const chevronRect = chevronElement.getBoundingClientRect();
        // Use actual dropdown height if available, otherwise estimate
        const dropdownHeight = dropdownElement ? dropdownElement.offsetHeight : 180;
        const dropdownWidth = dropdownElement ? dropdownElement.offsetWidth : 150;
        
        // Check if dropdown would go below viewport bottom - add some margin for safety
        const spaceBelow = window.innerHeight - chevronRect.bottom - 20; // 20px margin
        const showAbove = spaceBelow < dropdownHeight;
        
        // Calculate position
        let left = chevronRect.right - dropdownWidth;
        let top = showAbove ? chevronRect.top - dropdownHeight - 5 : chevronRect.bottom + 5; // 5px gap from chevron
        
        // Ensure dropdown stays within viewport
        if (left < 10) left = 10;
        if (left + dropdownWidth > window.innerWidth - 10) left = window.innerWidth - dropdownWidth - 10;
        if (top < 10) top = 10;
        if (top + dropdownHeight > window.innerHeight - 10) top = window.innerHeight - dropdownHeight - 10;
        
        return { showAbove, left, top };
    };

    const handleFileDropdownToggle = (itemId, attachmentIdx) => {
        const key = `${itemId}_${attachmentIdx}`;
        if (fileDropdownOpen === key) {
            setFileDropdownOpen(null);
        } else {
            setFileDropdownOpen(key);
            // Calculate position after state update
            setTimeout(() => {
                const position = calculateDropdownPosition(itemId, attachmentIdx, dropdownRef.current);
                setDropdownPosition(prev => ({
                    ...prev,
                    [key]: { left: position.left, top: position.top }
                }));
                setDropdownPositionAbove(prev => ({
                    ...prev,
                    [key]: position.showAbove
                }));
            }, 0);
        }
    };

    const handleFileDownload = async (file) => {
        try {
            const downloadName = file.name || file.filename || 'download';
            const query = new URLSearchParams({
                cases: 'true',
                download: 'true',
                mailroom: 'true',
                download_name: downloadName,
            });

            if (file.file_id) {
                query.set('id', file.file_id);
            } else if (file.file_path) {
                query.set('key', file.file_path);
            } else {
                alert('File path not available for download');
                console.error('Missing file_path and file_id in file object:', file);
                return;
            }
            
            // Get the signed URL using the file ID or S3 key
            const signedUrlResponse = await fetch(
                `https://api.casedb.co/get-signed-url.php?${query.toString()}`,
                { credentials: 'include' }
            );
            const signedData = await signedUrlResponse.json();
            
            if (signedData.success && signedData.url) {
                // Create a temporary link and trigger download
                const link = document.createElement('a');
                link.href = signedData.url;
                link.download = downloadName;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                alert(signedData.message || 'Failed to generate download link');
            }
        } catch (error) {
            console.error('Error downloading file:', error);
            alert('Error downloading file: ' + error.message);
        }
        setFileDropdownOpen(null);
    };

    const handleFilePreview = (file) => {
        window.open(`/file/${file.file_id}`, '_blank');
        setFileDropdownOpen(null);
    };

    const handleFileRename = (file) => {
        setFileToRename(file);
        
        // Extract filename and extension like DocumentSection.js does
        const fileName = file.name || '';
        if (fileName.includes('.')) {
            const extension = fileName.split('.').pop();
            const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.'));
            setNewFileName(nameWithoutExt);
            setFileExtension(extension);
        } else {
            setNewFileName(fileName);
            setFileExtension('');
        }
        
        setShowRenameFileModal(true);
        setFileDropdownOpen(null);
    };

    const handleFileProperties = (file) => {
        setFileProperties(file);
        setShowPropertiesModal(true);
        setFileDropdownOpen(null);
    };

    const submitFileRename = async () => {
        if (!fileToRename || !newFileName.trim()) {
            alert('Please enter a valid filename');
            return;
        }

        setRenamingFile(true);
        try {
            await ensureCaseDbCsrfToken();
            // Append extension back to the filename like DocumentSection.js does
            const finalFileName = fileExtension ? `${newFileName.trim()}.${fileExtension}` : newFileName.trim();
            
            const response = await fetch('https://api.casedb.co/mailroom.php', {
                method: 'POST',
                credentials: 'include',
                headers: withCsrfHeaders({
                    'Content-Type': 'application/json'
                }, 'POST'),
                body: JSON.stringify({
                    action: 'rename_file',
                    file_id: fileToRename.file_id,
                    new_name: finalFileName
                })
            });

            const data = await response.json();
            
            if (data.success) {
                // Refresh mailroom to show updated filename
                fetchMail();
                setShowRenameFileModal(false);
                setFileToRename(null);
                setNewFileName('');
            } else {
                alert(data.message || 'Failed to rename file');
            }
        } catch (error) {
            console.error('Error renaming file:', error);
            alert('Error renaming file');
        } finally {
            setRenamingFile(false);
        }
    };

    return (
        <div className={`page-container ${navVariant === 'full' ? 'ab' : navVariant === 'compact' ? 'compact' : ''}`}>
            <div id="page-header">
                <h2>Inbox</h2>
                {inboxNav === 0 && <span className='subtext'>You have <span style={{color: 'var(--fill)'}}>{notifications.length}</span> unseen notifications</span>}
                {inboxNav === 1 && 
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                        <span className='subtext'>Company fax number: <span style={{color: 'var(--fill)'}}>(713) 655-1587</span></span>

                        <span className='subtext'>Company SMS number: <span style={{color: 'var(--fill)'}}>(713) 221-3838</span></span>
                    </div>
                }
                {inboxNav === 2 && <span className='subtext'>Review inbound mail, AI extraction, case matches, and filing status</span>}
            </div>
            <div id='template-header'>
                {screenWidth < 729 ? (
                    <select className='default-select' value={inboxNav} onChange={(e) => setInboxNav(Number(e.target.value))}>
                        <option value={0}>Notifications</option>
                        <option value={1}>Mail Room</option>
                        <option value={2}>Mail</option>
                    </select>
                )  : (
                    <>
                        <h4 onClick={() => setInboxNav(0)} className={`${inboxNav !== 0 ? 'in' : ''}active`}>Notifications</h4>
                        <h4 onClick={() => setInboxNav(1)} className={`${inboxNav !== 1 ? 'in' : ''}active`}>Mail Room</h4>
                        <h4 onClick={() => setInboxNav(2)} className={`${inboxNav !== 2 ? 'in' : ''}active`}>Mail</h4>
                    </>
                )}
            </div>
            
            {inboxNav === 0 && (
                <>
                    {notifications.length > 0 ? (
                        <div className='notification-container'>
                            <div className='notification-section-heading'>
                                <h4 className='noti-identifier'>Notifications</h4>
                                <button
                                    type='button'
                                    className='action tertiary notification-mark-all-read'
                                    onClick={markAllNotificationsRead}
                                    disabled={markingAllRead}
                                >
                                    <CheckSquare size={16} />
                                    {markingAllRead ? 'Marking read…' : 'Mark All Read'}
                                </button>
                            </div>
                            {markAllReadError && (
                                <div className='notification-action-error' role='alert'>{markAllReadError}</div>
                            )}
                            {[...notifications].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map((notification, index) => (
                                <div
                                    key={index}
                                    className={`notification-item ${notification.url ? 'linked' : 'disabled'}`}
                                    onClick={() => {
                                        handleNotificationClick(notification);
                                        readNotification(notification);
                                    }}
                                    style={{cursor: notification.url ? 'pointer' : 'default'}}
                                >
                                    <div className='notification-details'>
                                        <span className='notification-list-icon'>
                                            {getNotificationTypeIcon(notification.type, 16, notification)}
                                        </span>
                                        <span className="notification-message">
                                            {notification.title}{notification?.case_name && ` (Case: ${notification?.case_name})`}
                                        </span>
                                        <span className="notification-time subtext">
                                            {getTimeAgo(notification.created_at)}
                                        </span>
                                        {notification.url && (
                                            <span className="notification-link subtext">
                                                {notification.url.startsWith("/") ? `...${notification.url}` : `.../${notification.url}`}
                                            </span>
                                        )}
                                    </div>
                                    <div className="notification-content">
                                        <span 
                                            className="notification-message subtext"
                                            dangerouslySetInnerHTML={{ __html: formatContentWithMentions(notification.body) }}
                                        />
                                    </div>
                                    <div className='subtext notification-knowledge'>
                                        {notification.url && <Link size={18} />}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <span className='subtext'>
                            No notifications
                        </span>
                    )}
                    {seenNotifications.length > 0 && (
                        <div className='notification-container'>
                            <h4 className='noti-identifier'>Previous Notifications</h4>
                            {[...seenNotifications].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map((notification, index) => (
                                <div
                                    key={index}
                                    className={`notification-item seen ${notification.url ? 'linked' : 'disabled'}`}
                                    onClick={() => handleNotificationClick(notification)}
                                    style={{cursor: notification.url ? 'pointer' : 'default'}}
                                >
                                    <div className='notification-details'>
                                        <span className='notification-list-icon'>
                                            {getNotificationTypeIcon(notification.type, 16, notification)}
                                        </span>
                                        <span className="notification-message">
                                            {notification.title}
                                        </span>
                                        <span className="notification-time subtext">
                                            {getTimeAgo(notification.created_at)}
                                        </span>
                                        {notification.url && (
                                            <span className="notification-link subtext">
                                                <span>{notification.url.startsWith("/") ? `...${notification.url}` : `.../${notification.url}`}</span>
                                                <span>{notification?.case_name && `(${notification?.case_name})`}</span>
                                            </span>
                                        )}
                                    </div>
                                    <span 
                                        className="notification-message subtext"
                                        dangerouslySetInnerHTML={{ __html: formatContentWithMentions(notification.body) }}
                                    />
                                    <div className='subtext notification-knowledge'>
                                        {notification.url && <Link size={18} />}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}

            {inboxNav === 1 && (
                <div className='mailroom-container'>
                    <div className='mailroom-actions'>
                        <MiniNav options={["All", ...new Set(mail.map(item => item.type.charAt(0).toUpperCase() + item.type.slice(1)))]} selectedOption={filterFeedType} setSelectedOption={setFilterFeedType} />
                        <button className='action tertiary' onClick={() => handleCommunicationClick('fax')} style={{ width: '100%' }}>
                            Send a Fax
                        </button>
                        <button className='action tertiary' onClick={() => handleCommunicationClick('sms')} style={{ width: '100%' }}>
                            Send a Text
                        </button>
                    </div>
                    <div className='notification-container'>
                        {loadingMail ? (
                            <div className="activity-item">
                                <div className="activity-content">
                                    <div className="activity-text subtext">
                                        Loading unassigned communications...
                                    </div>
                                </div>
                            </div>
                        ) : mail.length > 0 ? (
                            mail.filter(item => filterFeedType === 0 || item.type.charAt(0).toUpperCase() + item.type.slice(1) === Array.from(new Set(mail.map(item => item.type.charAt(0).toUpperCase() + item.type.slice(1))))[filterFeedType - 1]).map((item, index) => (
                                <div 
                                    key={index} 
                                    className="activity-item"
                                >
                                        <div className="activity-header">
                                            <div className="activity-left">
                                                <div className="activity-avatar">
                                                    {getTypeIcon(item.type)}
                                                </div>
                                                <div className="activity-meta">
                                                    <div className="activity-author">
                                                        <span className="activity-name">
                                                            {item.type === 'faxes' && 'Fax'}
                                                            {item.type === 'texts' && 'SMS'}
                                                            {item.type === 'calls' && 'Call'}
                                                            {item.type === 'emails' && 'Email'}
                                                            {!['faxes', 'texts', 'calls', 'emails'].includes(item.type) && item.type}
                                                        </span>
                                                        {(item.send_number || item.metadata?.phone_number) && (
                                                            <span className="activity-number subtext">
                                                                from {item.send_number || item.metadata?.phone_number}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="activity-time subtext">
                                                        {getTimeAgo(item.creation_date)}
                                                        {item.external_id && (
                                                            <span className="activity-external-id"> • ID: {item.external_id}</span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="activity-assignment">
                                                <button 
                                                    className="activity-action-btn assign-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleAssignClick(item);
                                                    }}
                                                    title="Assign to Case or Lead"
                                                >
                                                    <UserCheck size={16} />
                                                </button>
                                                <button 
                                                    className="activity-action-btn delete-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (window.confirm("Are you sure you want to delete this item?")) {
                                                            deleteMailItem(item.id);
                                                        }
                                                    }}
                                                    title="Delete Item"
                                                >
                                                    <X size={16} />
                                                </button>
                                            </div>
                                        </div>
                                        
                                        {item.subject && (
                                            <div className="activity-subject">
                                                <strong>{item.subject}</strong>
                                            </div>
                                        )}
                                        
                                        {item.content && (
                                            <div className="activity-content">
                                                <div 
                                                    className="activity-text"
                                                    dangerouslySetInnerHTML={{ __html: formatContentWithMentions(item.content) }}
                                                />
                                            </div>
                                        )}

                                    {item.attachments && item.attachments.length > 0 && (
                                        <div className="activity-attachments">
                                            <div className="activity-attachments-header">
                                                <Paperclip size={14} />
                                                <span>{item.attachments.length} attachment{item.attachments.length !== 1 ? 's' : ''}</span>
                                            </div>
                                            <div className="activity-attachments-list">
                                                {item.attachments.map((attachment, idx) => (
                                                    <div key={idx} className="activity-attachment" style={{position: 'relative'}}>
                                                        {typeof attachment === 'object' && attachment.file_id ? (
                                                            <>
                                                                <a 
                                                                    href={`/file/${attachment.file_id}`} 
                                                                    target="_blank" 
                                                                    rel="noopener noreferrer"
                                                                    className="activity-attachment-name activity-attachment-link"
                                                                >
                                                                    {attachment.name || attachment.filename || `Attachment ${idx + 1}`}
                                                                </a>
                                                                <span 
                                                                    className='activity-attachment-chevron'
                                                                    data-dropdown-id={`${item.id}_${idx}`}
                                                                    onClick={() => handleFileDropdownToggle(item.id, idx)}
                                                                    style={{cursor: 'pointer'}}
                                                                >
                                                                    <ChevronDown size={16} />
                                                                </span>
                                                                {fileDropdownOpen === `${item.id}_${idx}` && (
                                                                    <div 
                                                                        ref={dropdownRef}
                                                                        className={`file-dropdown-menu ${dropdownPositionAbove[`${item.id}_${idx}`] ? 'position-above' : ''}`} 
                                                                        style={{
                                                                            position: 'fixed',
                                                                            left: dropdownPosition[`${item.id}_${idx}`]?.left || 0,
                                                                            top: dropdownPosition[`${item.id}_${idx}`]?.top || 0,
                                                                            backgroundColor: 'var(--background-color)',
                                                                            border: '1px solid var(--border-color)',
                                                                            borderRadius: '12px',
                                                                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                                                            zIndex: 1000,
                                                                            minWidth: '150px'
                                                                        }}
                                                                    >
                                                                        <div 
                                                                            className="file-dropdown-item"
                                                                            onClick={() => handleFileDownload(attachment)}
                                                                            style={{
                                                                                padding: '8px 12px',
                                                                                cursor: 'pointer',
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                gap: '8px',
                                                                                fontSize: '0.9em'
                                                                            }}
                                                                        >
                                                                            <Download size={14} /> Download
                                                                        </div>
                                                                        <div 
                                                                            className="file-dropdown-item"
                                                                            onClick={() => handleFilePreview(attachment)}
                                                                            style={{
                                                                                padding: '8px 12px',
                                                                                cursor: 'pointer',
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                gap: '8px',
                                                                                fontSize: '0.9em'
                                                                            }}
                                                                        >
                                                                            <Eye size={14} /> Preview
                                                                        </div>
                                                                        <div 
                                                                            className="file-dropdown-item"
                                                                            onClick={() => handleFileRename(attachment)}
                                                                            style={{
                                                                                padding: '8px 12px',
                                                                                cursor: 'pointer',
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                gap: '8px',
                                                                                fontSize: '0.9em'
                                                                            }}
                                                                        >
                                                                            <Edit2 size={14} /> Rename
                                                                        </div>
                                                                        <div 
                                                                            className="file-dropdown-item"
                                                                            onClick={() => handleFileProperties(attachment)}
                                                                            style={{
                                                                                padding: '8px 12px',
                                                                                cursor: 'pointer',
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                gap: '8px',
                                                                                fontSize: '0.9em'
                                                                            }}
                                                                        >
                                                                            <Info size={14} /> Properties
                                                                        </div>
                                                                    </div>
                                                                )}
                                                            </>
                                                        ) : (
                                                            <span className="activity-attachment-name">
                                                                {typeof attachment === 'object' 
                                                                    ? (attachment.name || attachment.filename || `Attachment ${idx + 1}`)
                                                                    : `File ID: ${attachment}`
                                                                }
                                                            </span>
                                                        )}
                                                        {typeof attachment === 'object' && attachment.size && (
                                                            <span className="activity-attachment-size subtext">
                                                                ({attachment.size})
                                                            </span>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Display existing replies */}
                                    {item.replies && item.replies.length > 0 && (
                                        <div className="mailroom-activity-replies">
                                            <div className="activity-replies-header subtext">
                                                {item.replies.length} {item.replies.length === 1 ? 'Reply' : 'Replies'}
                                            </div>
                                            {item.replies.map((reply, replyIdx) => (
                                                <div key={replyIdx} className="mailroom-activity-reply">
                                                    <div className="activity-reply-header">
                                                        <span className="activity-reply-author subtext">{reply.author_name} (<span style={{ color: 'var(--fill)' }}>@{reply.author_user}</span>)</span>
                                                        <span className="activity-reply-time subtext">{getTimeAgo(reply.creation_date)}</span>
                                                    </div>
                                                    <div 
                                                        className="mailroom-activity-reply-content"
                                                        dangerouslySetInnerHTML={{ __html: formatContentWithMentions(reply.content) }}
                                                    />
                                                    {reply.attachments && reply.attachments.length > 0 && (
                                                        <div className="activity-reply-attachments">
                                                            {reply.attachments.map((att, attIdx) => (
                                                                <a 
                                                                    key={attIdx}
                                                                    href={`/file/${att.file_id || att.id}`} 
                                                                    target="_blank" 
                                                                    rel="noopener noreferrer"
                                                                    className="activity-reply-attachment subtext"
                                                                >
                                                                    <Paperclip size={12} />
                                                                    {att.name || att.filename || `File ${attIdx + 1}`}
                                                                </a>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Reply input */}
                                    <div className="activity-reply-input form-group nm full">
                                        {(!replyExpanded || replyingTo !== item.id) ? (
                                            <input
                                                type="text"
                                                placeholder="Add a response"
                                                className="reply-input-compact"
                                                onFocus={() => handleReplyFocus(item.id)}
                                                disabled={sendingReply}
                                            />
                                        ) : (
                                            <div className="reply-input-expanded">
                                                <textarea
                                                    value={replyContent}
                                                    onChange={(e) => setReplyContent(e.target.value)}
                                                    placeholder="Type your response here..."
                                                    className="reply-textarea"
                                                    rows={3}
                                                    autoFocus
                                                    disabled={sendingReply}
                                                />
                                                <div className="reply-actions">
                                                    <button
                                                        className="action"
                                                        onClick={() => handleReplySubmit(item.id)}
                                                        disabled={!replyContent.trim() || sendingReply}
                                                        style={{ opacity: (!replyContent.trim() || sendingReply) ? 0.5 : 1 }}
                                                    >
                                                        {sendingReply ? 'Sending...' : 'Respond'}
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/*item.metadata && typeof item.metadata === 'object' && Object.keys(item.metadata).length > 0 && (
                                        <div className="activity-metadata">
                                            <div className="activity-metadata-header subtext">Additional Info:</div>
                                            {Object.entries(item.metadata).map(([key, value]) => (
                                                <div key={key} className="activity-metadata-item subtext">
                                                    <strong>{key.replace(/_/g, ' ')}:</strong> {Array.isArray(value) ? value.join(', ') : String(value)}
                                                </div>
                                            ))}
                                        </div>
                                    )*/}
                                </div>
                            ))
                        ) : (
                            <div className="activity-item">
                                <div className="activity-content">
                                    <div className="activity-text subtext">
                                        No unassigned communications found. All messages have been properly assigned to cases or leads.
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {inboxNav === 2 && <MailIntake />}

            {/* Communication Modal */}
            {showCommunicationModal && (
                <Modal
                    title={`Send ${communicationType === 'fax' ? 'Fax' : 'Text Message'}`}
                    showOverflow={true}
                    instructions={`Send a ${communicationType === 'fax' ? 'fax' : 'text message'} to a phone number.`}
                    onClose={() => {
                        if (!sendingCommunication) {
                            setShowCommunicationModal(false);
                        }
                    }}
                    overflowy
                    footer={
                        <div className='modal-footer-actions'>
                            <button 
                                onClick={sendCommunication} 
                                className="action"
                                disabled={sendingCommunication || smsPendingConfirmation || !communicationData.phoneNumber || !communicationData.content || (communicationType === 'fax' && communicationData.files.length === 0)}
                                style={{opacity: (!sendingCommunication && !smsPendingConfirmation && communicationData.phoneNumber && communicationData.content && (communicationType !== 'fax' || communicationData.files.length > 0)) ? 1 : 0.5}}
                            >
                                {sendingCommunication
                                    ? `Sending ${communicationType === 'fax' ? 'Fax' : 'Text'}...`
                                    : smsPendingConfirmation
                                        ? 'Checking delivery...'
                                        : `Send ${communicationType === 'fax' ? 'Fax' : 'Text'}`}
                            </button>
                            <button 
                                onClick={() => {
                                    if (!sendingCommunication) {
                                        setShowCommunicationModal(false);
                                    }
                                }} 
                                className="action alt"
                                disabled={sendingCommunication}
                                style={{opacity: sendingCommunication ? 0.5 : 1}}
                            >
                                Cancel
                            </button>
                        </div>
                    }
                >
                    <div className='modal-content-wrapper'>
                        {communicationType === 'sms' && smsPendingConfirmation && (
                            <div
                                className="small-text"
                                role="status"
                                style={{
                                    padding: '10px 12px',
                                    borderRadius: '6px',
                                    backgroundColor: 'var(--background-color)'
                                }}
                            >
                                {SMS_CONFIRMATION_PENDING_MESSAGE}
                            </div>
                        )}
                        <div className='form-group'>
                            <label className="subtext">{communicationType === 'fax' ? 'Fax Number' : 'Phone Number'}</label>
                            <Text
                                type="text"
                                placeholder={communicationType === 'fax' ? '(713) 555-0123' : '(713) 555-0123'}
                                value={communicationData.phoneNumber}
                                onChange={(value) => setCommunicationData(prev => ({ ...prev, phoneNumber: value }))}
                            />
                        </div>

                        <div className='form-group'>
                            <label className="subtext">Subject (Optional)</label>
                            <Text
                                type="text"
                                placeholder={communicationType === 'fax' ? 'Fax subject' : 'Text message subject'}
                                value={communicationData.subject}
                                onChange={(value) => setCommunicationData(prev => ({ ...prev, subject: value }))}
                            />
                        </div>

                        <div className='form-group'>
                            <label className="subtext">Content</label>
                            <Text
                                type="textarea"
                                placeholder={communicationType === 'fax' ? 'Fax content or description' : 'Your message'}
                                value={communicationData.content}
                                onChange={(value) => setCommunicationData(prev => ({ ...prev, content: value }))}
                            />
                        </div>

                        <div className='form-group'>
                            <label className="subtext">
                                {communicationType === 'fax' ? 'File (Required)' : 'Attachments (Optional)'}
                            </label>
                            <input
                                type="file"
                                multiple={communicationType !== 'fax'}
                                accept={communicationType === 'fax' ? '.pdf,.doc,.docx,.jpg,.jpeg,.png,.tiff' : '*'}
                                onChange={handleFileUpload}
                                style={{ display: 'none' }}
                                id="file-upload-input"
                            />
                            <button
                                type="button"
                                onClick={() => document.getElementById('file-upload-input').click()}
                                className="action quaternary"
                                style={{
                                    width: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px',
                                    padding: '12px',
                                    marginBottom: communicationData.files.length > 0 ? '8px' : '0',
                                    backgroundColor: 'var(--background-color)'
                                }}
                            >
                                <Paperclip size={16} />
                                {communicationType === 'fax' ? 'Choose File to Fax' : 'Choose Files to Attach'}
                            </button>
                            {communicationData.files.length > 0 && (
                                <div style={{ 
                                    padding: '8px', 
                                    backgroundColor: 'var(--border-color)', 
                                    borderRadius: '6px',
                                    fontSize: '0.9em' 
                                }}>
                                    <div style={{ fontWeight: '500', marginBottom: '4px', color: 'var(--text-color)' }}>
                                        Selected {communicationType === 'fax' ? 'File' : 'Files'}:
                                    </div>
                                    {communicationData.files.map((file, index) => (
                                        <div key={index} style={{ 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'space-between',
                                            padding: '4px 0',
                                            color: 'var(--text-secondary)'
                                        }}>
                                            <span>{file.name}</span>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setCommunicationData(prev => ({
                                                        ...prev,
                                                        files: prev.files.filter((_, i) => i !== index)
                                                    }));
                                                }}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    color: 'var(--text-secondary)',
                                                    cursor: 'pointer',
                                                    padding: '2px'
                                                }}
                                                title="Remove file"
                                            >
                                                <X size={14} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className='form-group'>
                            <label className="subtext">Assign to Case or Lead (Optional)</label>
                            <EnhancedSearch 
                                expanded={true} 
                                modal={true} 
                                onChange={(result) => setCommunicationData(prev => ({ ...prev, selectedCase: result }))}
                            />
                            {communicationData.selectedCase && (
                                <div style={{ marginTop: '8px', fontSize: '0.9em', color: 'var(--text-secondary)' }}>
                                    Selected: {communicationData.selectedCase.label} ({communicationData.selectedCase.type})
                                </div>
                            )}
                        </div>
                    </div>
                </Modal>
            )}

            {/* Assignment Modal */}
            {showAssignModal && (
                <Modal
                    title="Assign Communication"
                    showOverflow={true}
                    instructions={`Assign this ${assigningItem?.type?.slice(0, -1) || 'communication'} to a case or lead.`}
                    onClose={() => {
                        if (!assigningInProgress) {
                            setShowAssignModal(false);
                            setAssigningItem(null);
                            setSelectedAssignment(null);
                        }
                    }}
                    overflowy
                    footer={
                        <div className='modal-footer-actions'>
                            <button 
                                onClick={assignMailItem} 
                                className="action"
                                disabled={!selectedAssignment || assigningInProgress}
                                style={{opacity: (selectedAssignment && !assigningInProgress) ? 1 : 0.5}}
                            >
                                {assigningInProgress ? 'Assigning...' : 'Assign'}
                            </button>
                            <button 
                                onClick={() => {
                                    if (!assigningInProgress) {
                                        setShowAssignModal(false);
                                        setAssigningItem(null);
                                        setSelectedAssignment(null);
                                    }
                                }} 
                                className="action alt"
                                disabled={assigningInProgress}
                                style={{opacity: assigningInProgress ? 0.5 : 1}}
                            >
                                Cancel
                            </button>
                        </div>
                    }
                >
                    <div className='modal-content-wrapper'>
                        {/* Item being assigned */}
                        <div className="assignment-item-preview">
                            <div className="activity-item" style={{marginBottom: '20px'}}>
                                <div className="activity-header">
                                    <div className="activity-left">
                                        <div className="activity-avatar">
                                            {assigningItem && getTypeIcon(assigningItem.type)}
                                        </div>
                                        <div className="activity-meta">
                                            <div className="activity-author">
                                                <span className="activity-name">
                                                    {assigningItem?.type === 'faxes' && 'Fax'}
                                                    {assigningItem?.type === 'texts' && 'SMS'}
                                                    {assigningItem?.type === 'calls' && 'Call'}
                                                    {assigningItem?.type === 'emails' && 'Email'}
                                                </span>
                                                {(assigningItem?.send_number || assigningItem?.metadata?.phone_number) && (
                                                    <span className="activity-number subtext">
                                                        from {assigningItem.send_number || assigningItem.metadata?.phone_number}
                                                    </span>
                                                )}
                                            </div>
                                            <div className="activity-time subtext">
                                                {assigningItem && getTimeAgo(assigningItem.creation_date)}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                {assigningItem?.subject && (
                                    <div className="activity-subject">
                                        <strong>{assigningItem.subject}</strong>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Search for case or lead */}
                        <div className='form-group nm'>
                            <label className="subtext">Search for Case or Lead</label>
                            <EnhancedSearch 
                                expanded={true} 
                                modal={true} 
                                onChange={(result) => setSelectedAssignment(result)}
                            />
                        </div>
                    </div>
                </Modal>
            )}

            {/* Rename File Modal */}
            {showRenameFileModal && (
                <Modal
                    title="Rename File"
                    instructions="Enter a new name for the file."
                    onClose={() => {
                        if (!renamingFile) {
                            setShowRenameFileModal(false);
                            setFileToRename(null);
                            setNewFileName('');
                        }
                    }}
                    footer={
                        <div className='modal-footer-actions'>
                            <button 
                                onClick={submitFileRename} 
                                className="action"
                                disabled={!newFileName.trim() || renamingFile}
                                style={{opacity: (newFileName.trim() && !renamingFile) ? 1 : 0.5}}
                            >
                                {renamingFile ? 'Renaming...' : 'Rename'}
                            </button>
                            <button 
                                onClick={() => {
                                    if (!renamingFile) {
                                        setShowRenameFileModal(false);
                                        setFileToRename(null);
                                        setNewFileName('');
                                    }
                                }} 
                                className="action alt"
                                disabled={renamingFile}
                                style={{opacity: renamingFile ? 0.5 : 1}}
                            >
                                Cancel
                            </button>
                        </div>
                    }
                >
                    <div className='modal-content-wrapper'>
                        <div className='form-group'>
                            <label className="subtext">File Name</label>
                            <div className='file-rename-container'>
                                <input
                                    type="text"
                                    value={newFileName}
                                    placeholder="Enter new file name"
                                    onChange={(e) => setNewFileName(e.target.value)}
                                />
                                {fileExtension && <span className="subtext" style={{ paddingBottom: "5px"}}>.{fileExtension}</span>}
                            </div>
                        </div>
                    </div>
                </Modal>
            )}

            {/* File Properties Modal */}
            {showPropertiesModal && fileProperties && (
                <Modal
                    title="File Properties"
                    instructions="View file details and metadata."
                    onClose={() => {
                        setShowPropertiesModal(false);
                        setFileProperties(null);
                    }}
                    footer={
                        <div className='modal-footer-actions'>
                            <button 
                                onClick={() => {
                                    setShowPropertiesModal(false);
                                    setFileProperties(null);
                                }} 
                                className="action"
                            >
                                Close
                            </button>
                        </div>
                    }
                >
                    <div className='modal-content-wrapper'>
                        <div className='form-group'>
                            <label className="subtext">File Name</label>
                            <div style={{padding: '8px', backgroundColor: 'var(--background-color)', border: '1px solid var(--border-color)', borderRadius: '6px'}}>
                                {fileProperties.name || 'N/A'}
                            </div>
                        </div>
                        <div className='form-group'>
                            <label className="subtext">File ID</label>
                            <div style={{padding: '8px', backgroundColor: 'var(--background-color)', border: '1px solid var(--border-color)', borderRadius: '6px'}}>
                                {fileProperties.file_id || 'N/A'}
                            </div>
                        </div>
                        {fileProperties.size && (
                            <div className='form-group'>
                                <label className="subtext">Size</label>
                                <div style={{padding: '8px', backgroundColor: 'var(--background-color)', border: '1px solid var(--border-color)', borderRadius: '6px'}}>
                                    {fileProperties.size}
                                </div>
                            </div>
                        )}
                        {fileProperties.file_path && (
                            <div className='form-group'>
                                <label className="subtext">File Path</label>
                                <div style={{padding: '8px', backgroundColor: 'var(--background-color)', border: '1px solid var(--border-color)', borderRadius: '6px', wordBreak: 'break-all'}}>
                                    {fileProperties.file_path}
                                </div>
                            </div>
                        )}
                    </div>
                </Modal>
            )}
        </div>
    );
};
